import { ReactQueryStaleTime } from '@openx/constants/react-query.ts';
import type { OptionsFetchQueryResult, OptionsList, OptionsMap, OptionsType } from '@openx/types/options.ts';
import type { UseQueryOptions } from '@openx/types/reactQuery.ts';
import { useQuery } from '@tanstack/react-query';

import { prepareMapBy } from '../../lib/prepareMapBy.ts';
import { produceSortedOptions } from '../../lib/sortedOptions.ts';
import { fetchOptions } from '../../state/options/api.ts';

const FALLBACK_LIST: OptionsList = [];
const FALLBACK_DATA: OptionsMap = {};

export const DEFAULT_KEY = 'id';

type UseOptionsFetchParams = {
  errorMessage: string;
  mapByKey?: string;
  optionType: OptionsType;
  optionParams?: Record<string, string | number>;
  queryOptions?: UseQueryOptions<OptionsFetchQueryResult>;
};

/**
 * @deprecated Use useOptionsFetchV3 instead
 */
export function useOptionsFetch({
  errorMessage,
  mapByKey = DEFAULT_KEY,
  optionType,
  optionParams,
  queryOptions = {},
}: UseOptionsFetchParams) {
  // eslint-disable-next-line @tanstack/query/exhaustive-deps
  const { isLoading, data, refetch } = useQuery<OptionsFetchQueryResult>({
    meta: {
      errorMessage,
    },
    queryFn: async () => {
      const options = await fetchOptions(optionType, optionParams);
      const mappedOptions = prepareMapBy(options, mapByKey);

      return {
        list: options,
        data: produceSortedOptions(mappedOptions),
      };
    },
    queryKey: [optionType],
    staleTime: ReactQueryStaleTime.MINUTES_15,
    ...queryOptions,
  });

  return {
    list: data?.list ?? FALLBACK_LIST,
    data: data?.data ?? FALLBACK_DATA,
    isLoading,
    refetch,
  };
}
